import { useState, useMemo } from 'react';
import { Button, Card, CardBody, Spinner, Chip } from '@heroui/react';
import { Scissors, AlertTriangle, ChevronDown, ChevronRight, PackageOpen, LayoutGrid, FileDown } from 'lucide-react';
import { toast } from 'sonner';
import { useRunNesting, useNestingCutList, useNestingResults } from '@/api/nesting';
import { useDownloadCutListPdf } from '@/api/pdf-generator';
import SheetLayoutDiagram from './SheetLayoutDiagram';

interface NestingTabProps {
  projectId: string;
}

export default function NestingTab({ projectId }: NestingTabProps) {
  const [expandedMaterials, setExpandedMaterials] = useState<Set<string>>(new Set());
  const [showLayouts, setShowLayouts] = useState<Set<string>>(new Set());

  const { data: cutList = [], isLoading } = useNestingCutList(projectId);
  const { data: nestingResults } = useNestingResults(projectId);
  const runNesting = useRunNesting();
  const downloadCutListPdf = useDownloadCutListPdf();

  const handleRunNesting = async () => {
    try {
      await runNesting.mutateAsync(projectId);
      toast.success('Nesting optimization complete');
    } catch {
      toast.error('Failed to run nesting optimization');
    }
  };

  const toggleMaterial = (materialId: string) => {
    setExpandedMaterials((prev) => {
      const next = new Set(prev);
      next.has(materialId) ? next.delete(materialId) : next.add(materialId);
      return next;
    });
  };

  const toggleLayouts = (materialId: string) => {
    setShowLayouts((prev) => {
      const next = new Set(prev);
      next.has(materialId) ? next.delete(materialId) : next.add(materialId);
      return next;
    });
  };

  // Cost mode lookup from nesting results
  const costModeMap = useMemo(() => {
    const map: Record<string, string> = {};
    if (nestingResults?.materials) {
      for (const m of Object.values(nestingResults.materials) as any[]) {
        if (m.costMode) map[m.materialId] = m.costMode;
      }
    }
    return map;
  }, [nestingResults]);

  // Summary stats
  const summary = useMemo(() => {
    if (cutList.length === 0) return null;

    const sheetEntries = cutList.filter((e) => e.materialType === 'sheet');
    const totalSheets = sheetEntries.reduce((sum, e) => sum + (e.sheetsNeeded ?? 0), 0);
    const totalCost = cutList.reduce((sum, e) => sum + e.totalCost, 0);
    const avgEfficiency =
      sheetEntries.length > 0
        ? sheetEntries.reduce((sum, e) => sum + (e.efficiency ?? 0), 0) / sheetEntries.length
        : 0;
    const materialCount = cutList.length;

    return { totalSheets, totalCost, avgEfficiency, materialCount };
  }, [cutList]);

  const cncCosts = nestingResults?.cncCosts;

  const getEfficiencyColor = (efficiency: number): 'success' | 'warning' | 'danger' => {
    if (efficiency >= 75) return 'success';
    if (efficiency >= 50) return 'warning';
    return 'danger';
  };

  const fmt = (n: number) => `$${n.toFixed(2)}`;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-[1400px]">
      {/* ─── Header Bar ───────────────────────────────────────────────── */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
            Nesting &amp; Cut List
          </h2>
          <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
            Optimize material usage with guillotine nesting
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="bordered"
            isLoading={downloadCutListPdf.isPending}
            isDisabled={cutList.length === 0}
            onPress={() => {
              downloadCutListPdf.mutate(projectId, {
                onSuccess: () => toast.success('Cut list PDF downloaded'),
                onError: () => toast.error('Failed to generate cut list PDF'),
              });
            }}
            startContent={!downloadCutListPdf.isPending ? <FileDown size={14} /> : undefined}
            className="border-[var(--border)] text-[var(--text-secondary)]"
          >
            Export PDF
          </Button>
          <Button
            color="primary"
            size="sm"
            onPress={handleRunNesting}
            isLoading={runNesting.isPending}
            startContent={!runNesting.isPending ? <Scissors size={14} /> : undefined}
          >
            Run Nesting Optimization
          </Button>
        </div>
      </div>

      {/* ─── Summary Cards ────────────────────────────────────────────── */}
      {summary && (
        <div className={`grid gap-4 mb-6 ${cncCosts ? 'grid-cols-5' : 'grid-cols-4'}`}>
          <Card className="bg-[var(--bg-surface)] border border-[var(--border-default)]">
            <CardBody className="p-4">
              <p className="text-[var(--text-muted)] text-xs uppercase mb-1">Total Sheets</p>
              <p className="text-[var(--text-primary)] font-mono text-lg font-semibold">
                {summary.totalSheets}
              </p>
            </CardBody>
          </Card>
          <Card className="bg-[var(--bg-surface)] border border-[var(--border-default)]">
            <CardBody className="p-4">
              <p className="text-[var(--text-muted)] text-xs uppercase mb-1">Material Cost</p>
              <p className="text-[var(--text-primary)] font-mono text-lg font-semibold">
                {fmt(summary.totalCost)}
              </p>
            </CardBody>
          </Card>
          <Card className="bg-[var(--bg-surface)] border border-[var(--border-default)]">
            <CardBody className="p-4">
              <p className="text-[var(--text-muted)] text-xs uppercase mb-1">Avg Efficiency</p>
              <p className="text-[var(--text-primary)] font-mono text-lg font-semibold">
                {summary.avgEfficiency.toFixed(1)}%
              </p>
            </CardBody>
          </Card>
          <Card className="bg-[var(--bg-surface)] border border-[var(--border-default)]">
            <CardBody className="p-4">
              <p className="text-[var(--text-muted)] text-xs uppercase mb-1">Materials</p>
              <p className="text-[var(--text-primary)] font-mono text-lg font-semibold">
                {summary.materialCount}
              </p>
            </CardBody>
          </Card>
          {cncCosts && (
            <Card className="bg-[var(--bg-surface)] border border-[var(--border-default)]">
              <CardBody className="p-4">
                <p className="text-[var(--text-muted)] text-xs uppercase mb-1">CNC Cost</p>
                <p className="text-[var(--text-primary)] font-mono text-lg font-semibold">
                  {fmt(cncCosts.totalCncCost)}
                </p>
                <p className="text-[var(--text-muted)] text-xs mt-0.5">
                  {cncCosts.totalTimeMin.toFixed(0)} min
                </p>
              </CardBody>
            </Card>
          )}
        </div>
      )}

      {/* ─── Cut List Table ───────────────────────────────────────────── */}
      {cutList.length === 0 ? (
        <div
          className="flex flex-col items-center justify-center py-20 gap-3"
          style={{ color: 'var(--text-muted)' }}
        >
          <PackageOpen size={40} strokeWidth={1} />
          <p className="text-sm">No nesting results yet.</p>
          <p className="text-xs">
            Add line items and materials to your project, then run nesting optimization.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {cutList.map((entry) => {
            const isExpanded = expandedMaterials.has(entry.materialId);
            const isSheet = entry.materialType === 'sheet';
            const isSkipped = entry.materialType === 'skipped';
            const layoutsVisible = showLayouts.has(entry.materialId);
            const matSheets = nestingResults?.materials?.[entry.materialId]?.sheets;
            const matCnc = cncCosts?.materials?.[entry.materialId];

            return (
              <div
                key={entry.materialId}
                className="rounded-lg border overflow-hidden"
                style={{ borderColor: 'var(--border)', backgroundColor: 'var(--bg-surface)' }}
              >
                {/* Material header row */}
                <button
                  onClick={() => toggleMaterial(entry.materialId)}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-[var(--bg-hover)]"
                >
                  <span style={{ color: 'var(--text-muted)' }}>
                    {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  </span>

                  <span
                    className="text-sm font-medium flex-1 truncate"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    {entry.materialName}
                  </span>

                  {isSheet && costModeMap[entry.materialId] === 'used_area' && (
                    <Chip size="sm" variant="flat" color="secondary" className="text-[10px]">
                      area
                    </Chip>
                  )}

                  {isSheet && entry.sheetSize && (
                    <span className="text-[var(--text-muted)] text-xs font-mono">
                      {entry.sheetSize}
                    </span>
                  )}

                  {isSheet && entry.sheetsNeeded != null && (
                    <span className="text-[var(--text-secondary)] text-xs">
                      {entry.sheetsNeeded} sheet{entry.sheetsNeeded !== 1 ? 's' : ''}
                    </span>
                  )}

                  {isSheet && entry.costPerSheet != null && (
                    <span className="text-[var(--text-muted)] text-xs font-mono">
                      {fmt(entry.costPerSheet)}/sheet
                    </span>
                  )}

                  {!isSheet && !isSkipped && (
                    <span className="text-[var(--text-secondary)] text-xs">
                      {entry.totalQty} {entry.unit}
                    </span>
                  )}

                  {matCnc && (
                    <span className="text-[var(--text-muted)] text-xs font-mono">
                      CNC: {fmt(matCnc.totalCncCost)}
                    </span>
                  )}

                  <span className="text-[var(--text-primary)] text-xs font-mono font-medium min-w-[80px] text-right">
                    {fmt(entry.totalCost)}
                  </span>

                  {isSheet && entry.efficiency != null && (
                    <Chip
                      size="sm"
                      variant="flat"
                      color={getEfficiencyColor(entry.efficiency)}
                      className="min-w-[60px] text-center"
                    >
                      {entry.efficiency.toFixed(1)}%
                    </Chip>
                  )}

                  {isSkipped && (
                    <Chip size="sm" variant="flat" color="warning" startContent={<AlertTriangle size={11} />}>
                      Skipped
                    </Chip>
                  )}
                </button>

                {/* Expanded parts list */}
                {isExpanded && entry.parts.length > 0 && (
                  <div
                    className="border-t"
                    style={{ borderColor: 'var(--border)', backgroundColor: 'var(--bg-base)' }}
                  >
                    {/* Parts header */}
                    <div
                      className="grid grid-cols-[1fr_140px_60px] px-4 py-1.5"
                      style={{ backgroundColor: 'var(--bg-panel)' }}
                    >
                      <span className="text-[var(--text-muted)] text-xs uppercase">Part</span>
                      <span className="text-[var(--text-muted)] text-xs uppercase text-right">
                        Size
                      </span>
                      <span className="text-[var(--text-muted)] text-xs uppercase text-right">
                        Qty
                      </span>
                    </div>

                    {entry.parts.map((part, idx) => {
                      const isLast = idx === entry.parts.length - 1;
                      const prefix = isLast ? '\u2514' : '\u251C';

                      return (
                        <div
                          key={`${part.sourceItemId}-${part.label}-${idx}`}
                          className="grid grid-cols-[1fr_140px_60px] px-4 py-1.5 border-t"
                          style={{ borderColor: 'var(--border)' }}
                        >
                          <span className="text-[var(--text-secondary)] text-xs">
                            <span className="text-[var(--text-muted)] font-mono mr-2">
                              {prefix}
                            </span>
                            {part.label}
                          </span>
                          <span className="text-[var(--text-primary)] font-mono text-xs text-right">
                            {part.width.toFixed(2)}&quot; &times; {part.height.toFixed(2)}&quot;
                          </span>
                          <span className="text-[var(--text-secondary)] text-xs text-right">
                            qty: {part.qty}
                          </span>
                        </div>
                      );
                    })}

                    {/* Sheet layout toggle (for sheet materials only) */}
                    {isSheet && matSheets && matSheets.length > 0 && (
                      <div
                        className="border-t px-4 py-2"
                        style={{ borderColor: 'var(--border)' }}
                      >
                        <Button
                          size="sm"
                          variant="ghost"
                          onPress={() => toggleLayouts(entry.materialId)}
                          startContent={<LayoutGrid size={13} />}
                          className="text-xs"
                        >
                          {layoutsVisible ? 'Hide' : 'Show'} Sheet Layouts ({matSheets.length})
                        </Button>
                      </div>
                    )}

                    {/* Sheet layout diagrams */}
                    {layoutsVisible && matSheets && (
                      <div
                        className="border-t px-4 py-4 space-y-4"
                        style={{ borderColor: 'var(--border)' }}
                      >
                        {matSheets.map((sheet, idx) => (
                          <SheetLayoutDiagram
                            key={idx}
                            sheet={sheet}
                            sheetIndex={idx}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {isExpanded && entry.parts.length === 0 && (
                  <div
                    className="border-t px-4 py-4 text-center text-xs"
                    style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}
                  >
                    No parts for this material.
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
